/** One coach question and its streamed reply: `coachAsk`, then the reply's own SSE stream (`coachStream`); the stored thread is re-read when it ends. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { CoachStreamEvent } from "@bsim/contracts";
import { useApi } from "../api/context";

export type TurnError = "provider_error" | "verifier_blocked" | "rate_limited" | "budget_blocked";
export interface Pending { readonly userText: string; readonly reply: string; readonly error: TurnError | null }

/** Copy per `coach.error` code (screens §P10). */
export const ERROR_COPY: Readonly<Record<TurnError, string>> = {
  provider_error: "Coach is unavailable. The concept cards still work.",
  verifier_blocked: "Let me put that as a question instead. What do you see in your own results that might explain it?",
  rate_limited: "Give it a moment.",
  budget_blocked: "Coach is paused for this session.",
};

/** The screens that are also a coach focus surface (CP-3 v2.35 `focus.surface`). */
const SURFACES = ["decide", "event", "results", "cockpit"] as const;
const surfaceOf = (screen: string): (typeof SURFACES)[number] | undefined => SURFACES.find((x) => x === screen);

export function useCoachTurn(teamId: string | null, context: { readonly screen: string; readonly decisionId?: string; readonly conceptId?: string }): { readonly pending: Pending | null; readonly busy: boolean; readonly ask: (text: string) => void } {
  const api = useApi();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Pending | null>(null);
  const source = useRef<EventSource | null>(null);
  useEffect(() => () => source.current?.close(), []);

  const finish = useCallback(() => {
    source.current?.close();
    source.current = null;
    void queryClient.invalidateQueries({ queryKey: ["coachHistory"] }).then(() => setPending((p) => (p?.error === null ? null : p)));
  }, [queryClient]);

  const ask = useCallback((text: string) => {
    if (teamId === null || text.trim() === "") return;
    setPending({ userText: text, reply: "", error: null });
    void (async () => {
      const res = await api.call("coachAsk", { teamId }, { text, ...(surfaceOf(context.screen) !== undefined ? { focus: { surface: surfaceOf(context.screen) as (typeof SURFACES)[number], ...(context.decisionId === undefined ? {} : { decisionId: context.decisionId as never }) } } : {}), context: { screen: context.screen, ...(context.decisionId === undefined ? {} : { decisionId: context.decisionId as never }), ...(context.conceptId === undefined ? {} : { conceptId: context.conceptId }) } }, { "idempotency-key": crypto.randomUUID() });
      if (!res.ok) { setPending({ userText: text, reply: "", error: res.status === 429 ? "rate_limited" : "provider_error" }); return; }
      if (typeof EventSource === "undefined") { setPending({ userText: text, reply: "", error: "provider_error" }); return; }
      const stream = new EventSource(res.value.streamPath);
      source.current = stream;
      stream.onmessage = (m: MessageEvent<string>) => {
        const event = JSON.parse(m.data) as CoachStreamEvent;
        if (event.type === "coach.delta") setPending((p) => (p === null ? p : { ...p, reply: p.reply + event.text }));
        else if (event.type === "coach.done") finish();
        else { stream.close(); setPending((p) => ({ userText: p?.userText ?? text, reply: "", error: event.code })); }
      };
      stream.onerror = () => { if (source.current === stream) { stream.close(); setPending((p) => (p !== null && p.reply === "" ? { ...p, error: "provider_error" } : p)); } };
    })();
  }, [api, context.conceptId, context.decisionId, context.screen, finish, teamId]);

  return { pending, busy: pending !== null && pending.error === null, ask };
}
